/** What the itinerary's cards show for a field beyond its stored value as text (plan §5): the
 *  read-only values from the detail payload (the cover, the operator's button behaviour, the
 *  import identity, the deep link and its QR code, the source, the catalog itinerary), the
 *  images downsized (the original on click), the picked records as links, the custom ID in
 *  mono, the App button's fields a behaviour does not use, and each linkable value's actions.
 *  Pure over the detail, except the deep link's Copy and QR code, which are controls, and
 *  Regenerate now where the deep link or its stored QR image is missing (D6: the page's
 *  Regenerate deep link, `deepLink.regenerate`), with Branch.io's progress under the link while
 *  the page waits on it (`deepLink.watch`). */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { Box, Button, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { Fragment, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { CTA_BUTTON_BEHAVIOR_LABELS, type ItineraryDetail } from '@/api/itineraries';
import type { ItineraryRecordFieldKey } from '@/api/itineraryRecord';
import { QrCodePopover } from '@/components/QrCodePopover';
import { useSnackbar } from '@/components/Snackbar';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { MONO_SX } from '@/features/bookings/record/panels/displays';
import {
  catalogTagActions,
  ctaTourActions,
  itineraryCustomIdActions,
  regionActions,
  tripTypeActions,
  webUrlActions,
  type CatalogCtx,
} from '@/features/catalog/catalogLinks';
import { BOOKING_URL, catalogSourceLabel, ctaVerdict, telNumber, type ItineraryCtaFacts } from '@/features/catalog/words';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';
import { ImageView } from '@/features/records/fields/widgets/ImageWidget';
import type { ImageChunks } from '@/utils/imageUrl';

import { DeepLinkWatchStatus, REGENERATE_NOW, type DeepLinkWatch } from '../actions/regenerateDeepLink';
import { ctaFacts } from '../parts/itineraryFactBandModel';

import { asBehaviour } from './appButton';

export interface ItineraryDisplayContext {
  detail: ItineraryDetail;
  links: CatalogCtx;
  now?: Date;
  /** Regenerate deep link (D6): `regenerate` opens its confirmation on the button (null: not
   *  offered to this operator here); `watch`, the regenerate the page is waiting on. */
  deepLink?: {
    regenerate: ((anchorEl: HTMLElement) => void) | null;
    watch: DeepLinkWatch | null;
  };
}

const INLINE_ICON_SX = { fontSize: 16, flex: 'none' } as const;

function mono(text: string, actions: ValueAction[]): ReactNode {
  return (
    <ValueActions actions={actions}>
      <Box component="span" sx={MONO_SX}>
        {text}
      </Box>
    </ValueActions>
  );
}

/** A stored image by its CDN keys, downsized, the original opening on click — on the image
 *  widget's view-mode row, so the edit row lands where it stood. */
export function storedImage(image: ImageChunks | null, label: string): ReactNode {
  return <ImageView image={image ? { id: 0, url: null, p: image.p, s: image.s } : null} label={label} />;
}

/** Picked records as links, "Southeast Asia, Indochina"; null when none. */
function refList<T extends { id: number; name?: string | null }>(
  refs: readonly T[],
  actions: (ref: T) => ValueAction[],
): ReactNode {
  if (refs.length === 0) return null;
  return refs.map((r, i) => (
    <Fragment key={r.id}>
      {i > 0 && ', '}
      <ValueActions actions={actions(r)}>{r.name?.trim() || `#${r.id}`}</ValueActions>
    </Fragment>
  ));
}

export { effectiveBehaviour, itineraryFieldNote, unusedCtaField } from './appButton';

/** The App button's facts as the card shows them: the stored ones in view mode; in edit mode
 *  the draft's — the tour named by the pick (the editor remembers its name) or the detail. */
export function useCtaFacts(detail: ItineraryDetail): ItineraryCtaFacts {
  const { mode, optionRef } = useRecordEditor();
  const { state } = useDraft();
  const stored = ctaFacts(detail);
  if (mode !== 'edit') return stored;
  const entries = state.entries[`itinerary:${detail.id}`] ?? {};
  const value = <K extends keyof ItineraryCtaFacts>(key: K, fallback: unknown): unknown =>
    entries[key] ? entries[key].to : fallback;
  const tourId = value('cta_tour', detail.cta_tour?.id ?? null);
  const tourName =
    typeof tourId === 'number'
      ? tourId === detail.cta_tour?.id
        ? stored.cta_tour?.name
        : optionRef('cta_tour', tourId)?.label || `Tour #${tourId}`
      : null;
  return {
    cta_button_text: (value('cta_button_text', stored.cta_button_text) as string | null) ?? null,
    cta_button_behavior: asBehaviour(value('cta_button_behavior', stored.cta_button_behavior)),
    booking_url: (value('booking_url', stored.booking_url) as string | null) ?? null,
    cta_tour: tourName ? { name: tourName } : null,
    cta_tour_departure: asBehaviour(value('cta_tour_departure', stored.cta_tour_departure)),
  };
}

/** The App button's verdict — one sentence, "inherit" resolved to the operator's behaviour
 *  (`ctaVerdict`) — across the card's first row; in edit mode it reads the draft, so it says what
 *  the button will do once saved. A combination the app cannot act on is an info note under it
 *  (an icon and words, never red) — only while the button has a text: without one the app shows
 *  no button (the server's rule too). */
export function CtaVerdictLine({ detail }: { detail: ItineraryDetail }) {
  const verdict = ctaVerdict(useCtaFacts(detail), detail.operator_cta_default);
  return (
    <Box data-cta-verdict sx={{ display: 'grid', gap: 0.25, pb: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }} aria-live="polite">
        {verdict.text}
      </Typography>
      {verdict.note && (
        <Typography
          variant="caption"
          color="text.secondary"
          role="note"
          sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
        >
          <InfoOutlinedIcon aria-hidden sx={{ ...INLINE_ICON_SX, fontSize: 14, mt: '1px' }} />
          {verdict.note}
        </Typography>
      )}
    </Box>
  );
}

/** The QR block without a stored image (none until Branch.io next writes the link). */
export const NO_STORED_QR = 'No stored image yet';

/** The deep link rows without a link. */
export const NO_DEEP_LINK = 'No deep link yet';

/** Regenerate now beside what is missing — the page's Regenerate deep link, its confirmation on
 *  this button. */
function RegenerateNow({ regenerate }: { regenerate: (anchorEl: HTMLElement) => void }) {
  return (
    <ImmediateActionButton onClick={(e) => regenerate(e.currentTarget)} sx={{ py: 0, minHeight: 24 }} data-regenerate-now>
      {REGENERATE_NOW}
    </ImmediateActionButton>
  );
}

/** What is missing, with Regenerate now when the page offers it. */
function missing(text: string, data: string, regenerate: ((anchorEl: HTMLElement) => void) | null | undefined): ReactNode {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
      <Typography component="span" variant="body2" color="text.secondary" {...{ [data]: true }}>
        {text}
      </Typography>
      {regenerate && <RegenerateNow regenerate={regenerate} />}
    </Box>
  );
}

/** Copies the deep link, saying so (the self-registration links' Copy). */
export function useCopyDeepLink(): (link: string) => void {
  const snackbar = useSnackbar();
  return (link) =>
    void navigator.clipboard.writeText(link).then(
      () => snackbar.show('Copied the deep link', 'success'),
      () => snackbar.show('Copy failed — clipboard unavailable', 'error'),
    );
}

/** The deep link: the link in mono (Open), and Copy — the tour's self-registration links'
 *  row pattern. */
function DeepLinkValue({ link }: { link: string }) {
  const copyLink = useCopyDeepLink();
  const copy = () => copyLink(link);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%', minWidth: 0 }}>
      <ValueActions actions={[act.external(link, 'Open the deep link')]}>
        <Box component="span" sx={{ ...MONO_SX, overflowWrap: 'anywhere' }} data-deep-link>
          {link}
        </Box>
      </ValueActions>
      <Tooltip title="Copy" describeChild>
        <IconButton size="small" onClick={copy} aria-label="Copy the deep link">
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/** The deep link's QR code: drawn from the link on request (click only — the code, Download
 *  PNG at print size), and the QR image stored on the media CDN, opened in a new tab (never
 *  generated on read: none until Branch.io next writes the link — Regenerate now). */
function QrCodeValue({
  detail,
  regenerate,
}: {
  detail: ItineraryDetail;
  regenerate: ((anchorEl: HTMLElement) => void) | null | undefined;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const link = detail.branchio_link?.trim() || null;
  const stored = detail.qr_image_url?.trim() || null;
  if (!link) return null;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<QrCode2Icon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
      >
        QR code
      </Button>
      {stored ? (
        <Link
          href={stored}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
        >
          Stored image
          <OpenInNewIcon aria-hidden sx={{ fontSize: 14 }} />
        </Link>
      ) : (
        missing(NO_STORED_QR, 'data-qr-missing', regenerate)
      )}
      <QrCodePopover
        value={anchor ? link : null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        title="QR code of the deep link"
        fileName={`itinerary-${detail.id}-deep-link.png`}
      />
    </Box>
  );
}

/** The catalog itinerary: its id, and the other itineraries showing the same days, each a
 *  link to its record. */
function catalogItinerary(d: ItineraryDetail): ReactNode {
  const { id, shared_with } = d.default_itinerary;
  if (id === null) return 'None yet';
  const others = [...new Set(shared_with)].filter((g) => g !== d.id);
  return (
    <>
      <ValueActions actions={[act.copy(String(id), 'Copy catalog itinerary id')]}>#{id}</ValueActions>
      {others.length > 0 && (
        <>
          {` · shared with ${others.length === 1 ? 'itinerary' : 'itineraries'} `}
          {others.map((g, i) => (
            <Fragment key={g}>
              {i > 0 && ', '}
              <Link component={RouterLink} to={`/itineraries/${g}`}>
                #{g}
              </Link>
            </Fragment>
          ))}
        </>
      )}
    </>
  );
}

/** A field's rendering in place of its stored value as text — every read-only field's, the
 *  images, the picked records with their links and the custom ID's (mono); undefined = the
 *  value as text. (The App button's unused fields say so in their caption:
 *  `itineraryFieldNote`.) */
export function itineraryFieldDisplay(key: ItineraryRecordFieldKey, c: ItineraryDisplayContext): ReactNode | undefined {
  const d = c.detail;
  switch (key) {
    case 'custom_id': {
      const v = d.custom_id?.trim();
      return v ? mono(v, itineraryCustomIdActions(v, c.links)) : null;
    }
    case 'cover_image':
      return storedImage(d.cover_image, 'Cover');
    case 'route_map':
      return storedImage(d.route_map, 'Route map');
    case 'cta_tour': {
      const tour = d.cta_tour;
      return tour ? (
        <ValueActions actions={ctaTourActions(tour, c.links)}>{tour.name?.trim() || `Tour #${tour.id}`}</ValueActions>
      ) : null;
    }
    case 'operator_cta_default':
      return d.operator_cta_default === null
        ? d.tenant
          ? 'Not set'
          : 'No operator'
        : (CTA_BUTTON_BEHAVIOR_LABELS[d.operator_cta_default] ?? `Behaviour ${d.operator_cta_default}`);
    case 'regions':
      return refList(d.regions, (r) => regionActions(r, c.links));
    case 'trip_types':
      return refList(d.trip_types, (t) => tripTypeActions(t, c.links));
    case 'tags':
      return refList(d.tags, (t) => catalogTagActions(t, c.links));
    case 'vehicle_route_set': {
      const set = d.vehicle_route_set;
      if (!set) return null;
      const name = set.name?.trim() || `Line set #${set.id}`;
      return (
        <ValueActions actions={[act.open(`/line-sets/${set.id}`, 'Open line set'), act.copy(name, 'Copy line set name')]}>
          {name}
        </ValueActions>
      );
    }
    case 'ext_id': {
      const v = d.ext_id?.trim();
      return v ? mono(v, [act.copy(v, 'Copy import identity')]) : null;
    }
    case 'branchio_link': {
      const v = d.branchio_link?.trim();
      const watch = c.deepLink?.watch ?? null;
      const value = v ? <DeepLinkValue link={v} /> : missing(NO_DEEP_LINK, 'data-deep-link-missing', c.deepLink?.regenerate);
      return watch ? (
        <Box component="span" sx={{ display: 'grid', minWidth: 0 }}>
          {value}
          <DeepLinkWatchStatus watch={watch} />
        </Box>
      ) : (
        value
      );
    }
    case 'qr_image_url':
      return d.branchio_link?.trim() ? <QrCodeValue detail={d} regenerate={c.deepLink?.regenerate} /> : NO_DEEP_LINK;
    case 'source':
      return catalogSourceLabel(d.source, c.now);
    case 'default_itinerary':
      return catalogItinerary(d);
    default:
      return undefined;
  }
}

/** A linkable editable value's actions, from its stored value; undefined = none. */
export function itineraryFieldActions(
  key: ItineraryRecordFieldKey,
): ((value: unknown) => ValueAction[]) | undefined {
  switch (key) {
    case 'name':
      return (v) => (typeof v === 'string' ? [act.copy(v, 'Copy name')] : []);
    case 'booking_url':
      return (v) => {
        if (typeof v !== 'string') return [];
        const number = telNumber(v);
        return number ? [act.tel(number), act.copy(v, 'Copy the tel: link')] : webUrlActions(v, `Open the ${BOOKING_URL}`);
      };
    default:
      return undefined;
  }
}
